import React from 'react';

/**
 * Comprehensive error handling utilities for purchase management system
 */

// Error types for purchase management operations
export const ERROR_TYPES = {
  VALIDATION: 'validation',
  API: 'api',
  NETWORK: 'network',
  AUTHENTICATION: 'authentication',
  AUTHORIZATION: 'authorization',
  BUSINESS_LOGIC: 'business_logic',
  SYSTEM: 'system'
};

// Error codes for purchase operations
export const PURCHASE_ERROR_CODES = {
  // Purchase Order Errors
  PURCHASE_ORDER_NOT_FOUND: 'PURCHASE_ORDER_NOT_FOUND',
  PURCHASE_ORDER_ALREADY_APPROVED: 'PURCHASE_ORDER_ALREADY_APPROVED',
  PURCHASE_ORDER_ALREADY_CANCELLED: 'PURCHASE_ORDER_ALREADY_CANCELLED',
  PURCHASE_ORDER_INVALID_STATUS: 'PURCHASE_ORDER_INVALID_STATUS',
  
  // Supplier Errors
  SUPPLIER_NOT_FOUND: 'SUPPLIER_NOT_FOUND',
  SUPPLIER_INACTIVE: 'SUPPLIER_INACTIVE',
  SUPPLIER_DUPLICATE_NAME: 'SUPPLIER_DUPLICATE_NAME',
  
  // Validation Errors
  INVALID_DATE_RANGE: 'INVALID_DATE_RANGE',
  INVALID_QUANTITY: 'INVALID_QUANTITY',
  INVALID_PRICE: 'INVALID_PRICE',
  MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',
  
  // Receipt Errors
  RECEIPT_QUANTITY_EXCEEDS_ORDERED: 'RECEIPT_QUANTITY_EXCEEDS_ORDERED',
  RECEIPT_ALREADY_COMPLETED: 'RECEIPT_ALREADY_COMPLETED',
  
  // Conversion Errors
  SALES_ORDER_NOT_FOUND: 'SALES_ORDER_NOT_FOUND',
  SALES_ORDER_ALREADY_CONVERTED: 'SALES_ORDER_ALREADY_CONVERTED',
  NO_PRODUCTS_TO_CONVERT: 'NO_PRODUCTS_TO_CONVERT',
  SUPPLIER_MAPPING_FAILED: 'SUPPLIER_MAPPING_FAILED',
  
  // Permission Errors
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
  APPROVAL_NOT_ALLOWED: 'APPROVAL_NOT_ALLOWED'
};

// Error messages mapping
export const ERROR_MESSAGES = {
  [PURCHASE_ERROR_CODES.PURCHASE_ORDER_NOT_FOUND]: '采购订单不存在',
  [PURCHASE_ERROR_CODES.PURCHASE_ORDER_ALREADY_APPROVED]: '采购订单已经被批准，无法重复操作',
  [PURCHASE_ERROR_CODES.PURCHASE_ORDER_ALREADY_CANCELLED]: '采购订单已被取消',
  [PURCHASE_ERROR_CODES.PURCHASE_ORDER_INVALID_STATUS]: '采购订单状态无效',
  
  [PURCHASE_ERROR_CODES.SUPPLIER_NOT_FOUND]: '供应商不存在',
  [PURCHASE_ERROR_CODES.SUPPLIER_INACTIVE]: '供应商未激活或已停用',
  [PURCHASE_ERROR_CODES.SUPPLIER_DUPLICATE_NAME]: '供应商名称已存在',
  
  [PURCHASE_ERROR_CODES.INVALID_DATE_RANGE]: '日期范围无效',
  [PURCHASE_ERROR_CODES.INVALID_QUANTITY]: '数量必须大于0',
  [PURCHASE_ERROR_CODES.INVALID_PRICE]: '价格必须大于0',
  [PURCHASE_ERROR_CODES.MISSING_REQUIRED_FIELD]: '缺少必填字段',
  
  [PURCHASE_ERROR_CODES.RECEIPT_QUANTITY_EXCEEDS_ORDERED]: '收货数量不能超过订购数量',
  [PURCHASE_ERROR_CODES.RECEIPT_ALREADY_COMPLETED]: '收货已完成，无法继续添加',
  
  [PURCHASE_ERROR_CODES.SALES_ORDER_NOT_FOUND]: '销售订单不存在',
  [PURCHASE_ERROR_CODES.SALES_ORDER_ALREADY_CONVERTED]: '销售订单已转换为采购单',
  [PURCHASE_ERROR_CODES.NO_PRODUCTS_TO_CONVERT]: '没有可转换的产品',
  [PURCHASE_ERROR_CODES.SUPPLIER_MAPPING_FAILED]: '供应商映射失败',
  
  [PURCHASE_ERROR_CODES.INSUFFICIENT_PERMISSIONS]: '权限不足',
  [PURCHASE_ERROR_CODES.APPROVAL_NOT_ALLOWED]: '当前状态不允许审批操作'
};

/**
 * Create a standardized error object
 */
export const createError = (code, message, type = ERROR_TYPES.BUSINESS_LOGIC, details = {}) => {
  return {
    code,
    message: message || ERROR_MESSAGES[code] || '未知错误',
    type,
    details,
    timestamp: new Date().toISOString()
  };
};

/**
 * Parse API error response and return standardized error
 */
export const parseApiError = (error) => {
  if (!error) {
    return createError('UNKNOWN_ERROR', '未知错误', ERROR_TYPES.SYSTEM);
  }

  // Handle network errors
  if (!error.response) {
    return createError('NETWORK_ERROR', '网络连接错误，请检查网络设置', ERROR_TYPES.NETWORK, {
      originalError: error.message
    });
  }

  const { status, data } = error.response;

  // Handle HTTP status codes
  switch (status) {
    case 400:
      return createError(
        data?.code || 'VALIDATION_ERROR',
        data?.error || data?.message || '请求参数错误',
        ERROR_TYPES.VALIDATION,
        { statusCode: status, responseData: data }
      );

    case 401:
      return createError(
        'AUTHENTICATION_ERROR',
        '身份验证失败，请重新登录',
        ERROR_TYPES.AUTHENTICATION,
        { statusCode: status }
      );

    case 403:
      return createError(
        PURCHASE_ERROR_CODES.INSUFFICIENT_PERMISSIONS,
        '权限不足，无法执行此操作',
        ERROR_TYPES.AUTHORIZATION,
        { statusCode: status }
      );

    case 404:
      return createError(
        data?.code || 'RESOURCE_NOT_FOUND',
        data?.error || '请求的资源不存在',
        ERROR_TYPES.API,
        { statusCode: status }
      );

    case 409:
      return createError(
        data?.code || 'CONFLICT_ERROR',
        data?.error || '数据冲突，请刷新后重试',
        ERROR_TYPES.BUSINESS_LOGIC,
        { statusCode: status }
      );

    case 422:
      return createError(
        data?.code || 'VALIDATION_ERROR',
        data?.error || '数据验证失败',
        ERROR_TYPES.VALIDATION,
        { statusCode: status, validationErrors: data?.validation_errors }
      );

    case 500:
      return createError(
        'SERVER_ERROR',
        '服务器内部错误，请稍后重试',
        ERROR_TYPES.SYSTEM,
        { statusCode: status }
      );

    case 503:
      return createError(
        'SERVICE_UNAVAILABLE',
        '服务暂时不可用，请稍后重试',
        ERROR_TYPES.SYSTEM,
        { statusCode: status }
      );

    default:
      return createError(
        'API_ERROR',
        data?.error || data?.message || `请求失败 (${status})`,
        ERROR_TYPES.API,
        { statusCode: status, responseData: data }
      );
  }
};

/**
 * Validation utilities for purchase management
 */
export const validators = {
  // Purchase order validation
  purchaseOrder: (data) => {
    const errors = [];

    if (!data.supplier_name?.trim()) {
      errors.push(createError(PURCHASE_ERROR_CODES.MISSING_REQUIRED_FIELD, '供应商名称是必填项', ERROR_TYPES.VALIDATION));
    }

    if (!data.products || !Array.isArray(data.products) || data.products.length === 0) {
      errors.push(createError(PURCHASE_ERROR_CODES.MISSING_REQUIRED_FIELD, '必须至少包含一个产品', ERROR_TYPES.VALIDATION));
    }

    if (data.products) {
      data.products.forEach((product, index) => {
        if (!product.product_name?.trim()) {
          errors.push(createError(PURCHASE_ERROR_CODES.MISSING_REQUIRED_FIELD, `产品 ${index + 1}: 产品名称是必填项`, ERROR_TYPES.VALIDATION));
        }

        if (!product.quantity || product.quantity <= 0) {
          errors.push(createError(PURCHASE_ERROR_CODES.INVALID_QUANTITY, `产品 ${index + 1}: 数量必须大于0`, ERROR_TYPES.VALIDATION));
        }

        // Allow 0 or empty unit_cost for draft POs
        // Unit price validation removed to allow drafts and negotiations
      });
    }

    if (data.expected_delivery_date) {
      const deliveryDate = new Date(data.expected_delivery_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (deliveryDate < today) {
        errors.push(createError(PURCHASE_ERROR_CODES.INVALID_DATE_RANGE, '预计交货日期不能早于今天', ERROR_TYPES.VALIDATION));
      }
    }

    return errors;
  },

  // Receipt validation
  receipt: (data, purchaseOrder) => {
    const errors = [];

    if (!data.receipt_date) {
      errors.push(createError(PURCHASE_ERROR_CODES.MISSING_REQUIRED_FIELD, '收货日期是必填项', ERROR_TYPES.VALIDATION));
    }

    if (!data.receipt_items || !Array.isArray(data.receipt_items) || data.receipt_items.length === 0) {
      errors.push(createError(PURCHASE_ERROR_CODES.MISSING_REQUIRED_FIELD, '必须至少包含一个收货项目', ERROR_TYPES.VALIDATION));
    }

    if (data.receipt_items && purchaseOrder?.products) {
      let hasItemsToReceive = false;
      
      data.receipt_items.forEach((item, index) => {
        const orderedProduct = purchaseOrder.products.find(p => p.product_code === item.product_code);
        
        if (!orderedProduct) {
          errors.push(createError(PURCHASE_ERROR_CODES.MISSING_REQUIRED_FIELD, `收货项目 ${index + 1}: 产品不在原订单中`, ERROR_TYPES.VALIDATION));
          return;
        }

        // Only validate items that have received_quantity > 0
        const receivedQty = parseFloat(item.received_quantity) || 0;
        if (receivedQty > 0) {
          hasItemsToReceive = true;
          
          if (receivedQty > (orderedProduct.quantity - (orderedProduct.received_quantity || 0))) {
            errors.push(createError(
              PURCHASE_ERROR_CODES.RECEIPT_QUANTITY_EXCEEDS_ORDERED, 
              `收货项目 ${index + 1}: 收货数量超过剩余订购数量`, 
              ERROR_TYPES.VALIDATION
            ));
          }
        }
      });
      
      // Check if at least one item has received_quantity > 0
      if (!hasItemsToReceive) {
        errors.push(createError(PURCHASE_ERROR_CODES.MISSING_REQUIRED_FIELD, '必须至少包含一个收货项目', ERROR_TYPES.VALIDATION));
      }
    }

    return errors;
  },

  // Supplier validation
  supplier: (data) => {
    const errors = [];

    if (!data.supplier_name?.trim()) {
      errors.push(createError(PURCHASE_ERROR_CODES.MISSING_REQUIRED_FIELD, '供应商名称是必填项', ERROR_TYPES.VALIDATION));
    }

    if (!data.contact_person?.trim()) {
      errors.push(createError(PURCHASE_ERROR_CODES.MISSING_REQUIRED_FIELD, '联系人是必填项', ERROR_TYPES.VALIDATION));
    }

    if (data.email && !/S+@S+.S+/.test(data.email)) {
      errors.push(createError('INVALID_EMAIL', '邮箱格式不正确', ERROR_TYPES.VALIDATION));
    }

    if (data.phone && !/^[0-9-]+$/.test(data.phone)) {
      errors.push(createError('INVALID_PHONE', '电话号码格式不正确', ERROR_TYPES.VALIDATION));
    }

    return errors;
  },

  // Date range validation
  dateRange: (startDate, endDate) => {
    const errors = [];

    if (!startDate || !endDate) {
      errors.push(createError(PURCHASE_ERROR_CODES.MISSING_REQUIRED_FIELD, '开始日期和结束日期都是必填项', ERROR_TYPES.VALIDATION));
      return errors;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      errors.push(createError(PURCHASE_ERROR_CODES.INVALID_DATE_RANGE, '开始日期不能晚于结束日期', ERROR_TYPES.VALIDATION));
    }

    const daysDiff = (end - start) / (1000 * 60 * 60 * 24);
    if (daysDiff > 365) {
      errors.push(createError(PURCHASE_ERROR_CODES.INVALID_DATE_RANGE, '日期范围不能超过365天', ERROR_TYPES.VALIDATION));
    }

    return errors;
  }
};

/**
 * Error notification utilities
 */
export const showError = (error, options = {}) => {
  const {
    showDetails = false,
    duration = 5000,
    onClose = null
  } = options;

  let message = error?.message || '发生未知错误';
  
  if (showDetails && error?.details) {
    message += `\n详细信息: ${JSON.stringify(error.details, null, 2)}`;
  }

  // You can integrate with your preferred notification system here
  // For now, we'll use console.error and alert as fallback
  console.error('Error occurred:', error);
  
  if (typeof window !== 'undefined' && window.alert) {
    alert(message);
  }

  if (onClose) {
    setTimeout(onClose, duration);
  }

  return error;
};

/**
 * Retry mechanism for failed operations
 */
export const retryOperation = async (operation, maxRetries = 3, delay = 1000) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = parseApiError(error);
      
      // Don't retry for certain error types
      if (
        lastError.type === ERROR_TYPES.VALIDATION ||
        lastError.type === ERROR_TYPES.AUTHORIZATION ||
        lastError.code === PURCHASE_ERROR_CODES.PURCHASE_ORDER_NOT_FOUND
      ) {
        throw lastError;
      }

      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
  }

  throw lastError;
};

/**
 * Error boundary for React components
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error boundary caught an error:', error, errorInfo);
    
    // Log to error reporting service if available
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>出现错误</h2>
          <p>页面遇到了一个错误，请刷新页面重试。</p>
          <button onClick={() => window.location.reload()}>
            刷新页面
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const errorHandler = {
  ERROR_TYPES,
  PURCHASE_ERROR_CODES,
  ERROR_MESSAGES,
  createError,
  parseApiError,
  validators,
  showError,
  retryOperation,
  ErrorBoundary
};

export default errorHandler;